import { Card, Divider, Spin, Tabs, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { legalConsentService } from '../../services/legalConsent.service';
import type { PublicLegalDocument } from '../../types/legalConsent.types';

const { Text } = Typography;

const LEGAL_CONTENT_STYLES = `
  .legal-content { line-height: 1.7; font-size: 14px; }
  .legal-content p { margin-top: 0 !important; margin-bottom: 12px !important; }
  .legal-content h1, .legal-content h2, .legal-content h3,
  .legal-content h4, .legal-content h5, .legal-content h6 {
    margin: 20px 0 8px !important;
    font-weight: 600 !important;
    line-height: 1.4 !important;
  }
  .legal-content ul, .legal-content ol {
    padding-left: 24px !important;
    margin-bottom: 12px !important;
  }
  .legal-content li { margin-bottom: 4px !important; }
  .legal-content strong, .legal-content b { font-weight: 600 !important; }
  .legal-content br { display: block !important; }
`;

function processLegalHtml(html: string): string {
  if (!/<(p|h[1-6]|div|ul|ol|li|blockquote)\b/i.test(html)) {
    return html.replace(/\n/g, '<br>');
  }
  return html;
}

function DocumentFooter({ doc }: { doc: PublicLegalDocument }) {
  const publishedDate = doc.publishedAt
    ? dayjs(doc.publishedAt).format('DD/MM/YYYY')
    : null;

  return (
    <>
      <Divider />
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Versão: <Text strong style={{ fontSize: 12 }}>{doc.version}</Text>
        </Text>
        {publishedDate && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Publicado em: <Text strong style={{ fontSize: 12 }}>{publishedDate}</Text>
          </Text>
        )}
      </div>
    </>
  );
}

export default function LegalPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [termsDoc, setTermsDoc] = useState<PublicLegalDocument | null>(null);
  const [privacyDoc, setPrivacyDoc] = useState<PublicLegalDocument | null>(null);
  const [cookieDoc, setCookieDoc] = useState<PublicLegalDocument | null>(null);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [loadingPrivacy, setLoadingPrivacy] = useState(true);
  const [loadingCookie, setLoadingCookie] = useState(true);

  const version = searchParams.get('version') || undefined;
  const activeTab = location.pathname.includes('politica-de-privacidade')
    ? 'privacidade'
    : location.pathname.includes('politica-de-cookies')
    ? 'cookies'
    : 'termos';

  useEffect(() => {
    const load = async () => {
      try {
        const data = await legalConsentService.getPublicTerms(version);
        setTermsDoc(data);
      } finally {
        setLoadingTerms(false);
      }
    };
    load();
  }, [version]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await legalConsentService.getPublicPrivacyPolicy(version);
        setPrivacyDoc(data);
      } finally {
        setLoadingPrivacy(false);
      }
    };
    load();
  }, [version]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await legalConsentService.getPublicCookiePolicy(version);
        setCookieDoc(data);
      } finally {
        setLoadingCookie(false);
      }
    };
    load();
  }, [version]);

  const handleTabChange = (key: string) => {
    const paths: Record<string, string> = {
      termos: '/legal/termos-de-uso',
      privacidade: '/legal/politica-de-privacidade',
      cookies: '/legal/politica-de-cookies',
    };
    const path = paths[key] ?? '/legal/termos-de-uso';
    navigate(version ? `${path}?version=${version}` : path);
  };

  return (
    <Card style={{ maxWidth: 900, margin: '40px auto' }}>
      <style>{LEGAL_CONTENT_STYLES}</style>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'termos',
            label: 'Termos de Uso',
            children: loadingTerms ? (
              <Spin style={{ display: 'block', margin: '40px auto' }} />
            ) : (
              <>
                <div
                  className="legal-content"
                  dangerouslySetInnerHTML={{
                    __html: processLegalHtml(termsDoc?.contentHtml || 'Documento indisponível.'),
                  }}
                />
                {termsDoc && <DocumentFooter doc={termsDoc} />}
              </>
            ),
          },
          {
            key: 'privacidade',
            label: 'Política de Privacidade',
            children: loadingPrivacy ? (
              <Spin style={{ display: 'block', margin: '40px auto' }} />
            ) : (
              <>
                <div
                  className="legal-content"
                  dangerouslySetInnerHTML={{
                    __html: processLegalHtml(privacyDoc?.contentHtml || 'Documento indisponível.'),
                  }}
                />
                {privacyDoc && <DocumentFooter doc={privacyDoc} />}
              </>
            ),
          },
          {
            key: 'cookies',
            label: 'Política de Cookies',
            children: loadingCookie ? (
              <Spin style={{ display: 'block', margin: '40px auto' }} />
            ) : (
              <>
                <div
                  className="legal-content"
                  dangerouslySetInnerHTML={{
                    __html: processLegalHtml(cookieDoc?.contentHtml || 'Documento indisponível.'),
                  }}
                />
                {cookieDoc && <DocumentFooter doc={cookieDoc} />}
              </>
            ),
          },
        ]}
      />
    </Card>
  );
}
